import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Plane, Clock, ArrowRight, Filter, ChevronDown, ChevronUp, Tag, MapPin, Calendar, Users, ArrowRightLeft, Search, ChevronLeft, ChevronRight, ArrowUpDown, Zap, Timer, X, Luggage, Utensils, CalendarDays, Baby, UserRound, CircleUser, Sparkles, Plus, Minus, AlertCircle } from 'lucide-react';
import { airports } from '../data/mock'; // Keep airports for now (static list)
import { fetchFareRulesForFlight } from '../utils/fareRulesRouter';
import { buildSelectedFlightPayload } from '../utils/fareSelection';
import { useAgentAuth } from '../context/AgentAuthContext';
import Footer from '../components/Footer';
import BottomSheet from '../components/BottomSheet';
import PullToRefresh from '../components/PullToRefresh';
import FilterSidebar from '../components/FilterSidebar';
import MobileFilterSheet from '../components/MobileFilterSheet';
import MobileModifySearch from '../components/MobileModifySearch';
import SortButtons from '../components/SortButtons';
import FlightCard from '../components/FlightCard';
import DesktopFlightDetails from '../components/DesktopFlightDetails';
import DesktopModifySearch from '../components/DesktopModifySearch';
import FareRulesCompact from '../components/FareRulesCompact';
import { getFareTypeInfo } from '../utils/fareTypeMapping';
import { enrichOfflineFlight } from '../utils/airportMapper';
import { fetchAirlineLogos } from '../utils/airlineLogoCache';
import { trackSearch } from '../utils/analytics';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import axios from 'axios';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { deriveCodeshare } from '../utils/tripjackDerive';
import { tripBaggageBySegmentFromPriceInfo } from '../utils/tripjackTripBaggage';
import { publicFareTypeLabel } from '../utils/acfFareDisplay';
import { rememberCurrentSearchUrl } from '../utils/lastSearchUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get refundability badge info
const getRefundabilityInfo = (refundableType) => {
  switch (refundableType) {
    case 1:
      return {
        label: 'Refundable',
        bgColor: 'bg-green-50',
        textColor: 'text-green-700',
        borderColor: 'border-green-200'
      };
    case 2:
      return {
        label: 'Partially Refundable',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-700',
        borderColor: 'border-yellow-200'
      };
    case 0:
    default:
      return {
        label: 'Non-Refundable',
        bgColor: 'bg-red-50',
        textColor: 'text-red-700',
        borderColor: 'border-red-200'
      };
  }
};

// Skeleton Loader Component for better perceived performance
const FlightSkeleton = () => (
  <div className="bg-white rounded-lg p-4 mb-4 animate-pulse">
    <div className="flex justify-between items-start mb-3">
      <div className="flex-1">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
      <div className="h-8 bg-gray-200 rounded w-24"></div>
    </div>
    <div className="flex justify-between items-center mb-3">
      <div className="h-10 bg-gray-200 rounded w-20"></div>
      <div className="flex-1 mx-4">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
      </div>
      <div className="h-10 bg-gray-200 rounded w-20"></div>
    </div>
    <div className="flex justify-between items-center">
      <div className="h-4 bg-gray-200 rounded w-32"></div>
      <div className="h-10 bg-teal-100 rounded w-32"></div>
    </div>
  </div>
);

// Desktop skeleton — wider card, matches result row geometry
const FlightSkeletonDesktop = () => (
  <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-5 animate-pulse">
    <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-center">
      {/* Airline */}
      <div className="flex items-center gap-3 w-44">
        <div className="w-10 h-10 rounded bg-gray-200"></div>
        <div className="space-y-1.5">
          <div className="h-3 bg-gray-200 rounded w-20"></div>
          <div className="h-2.5 bg-gray-200 rounded w-14"></div>
        </div>
      </div>
      {/* Timeline */}
      <div className="flex items-center gap-4">
        <div className="space-y-1.5">
          <div className="h-5 bg-gray-200 rounded w-16"></div>
          <div className="h-3 bg-gray-200 rounded w-12"></div>
        </div>
        <div className="flex-1 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-gray-200"></div>
          <div className="flex-1 h-[2px] bg-gray-200"></div>
          <div className="h-3 bg-gray-200 rounded w-16"></div>
          <div className="flex-1 h-[2px] bg-gray-200"></div>
          <div className="w-2 h-2 rounded-full bg-gray-200"></div>
        </div>
        <div className="space-y-1.5">
          <div className="h-5 bg-gray-200 rounded w-16"></div>
          <div className="h-3 bg-gray-200 rounded w-12"></div>
        </div>
      </div>
      {/* Price + CTA */}
      <div className="space-y-2 w-32 text-right">
        <div className="h-6 bg-gray-200 rounded w-24 ml-auto"></div>
        <div className="h-9 bg-teal-100 rounded-lg w-28 ml-auto"></div>
      </div>
    </div>
  </div>
);

// Main Component
const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Mini-Sprint 1 (Feb 2026) · Forward agent context to /search-all so
  // agent-scoped markup_rules are applied to every supplier card.
  const { agent: agentSession } = useAgentAuth();

  useEffect(() => {
    rememberCurrentSearchUrl();
  }, [location.pathname, location.search]);

  // Domestic India airports (used for routing /search → R/T pages on tripType=roundTrip)
  const DOMESTIC_AIRPORTS = ['DEL', 'BOM', 'BLR', 'MAA', 'CCU', 'HYD', 'AMD', 'PNQ', 'GOI', 'COK', 'JAI', 'LKO', 'TRV', 'IXC', 'GAU', 'IXB', 'ATQ', 'IXR', 'VNS', 'IDR', 'RPR', 'IXU', 'BBI', 'IXZ', 'IMF', 'SXR', 'IXJ', 'DIB', 'IXL', 'AGR', 'BDQ', 'STV', 'UDR'];

  // If a caller hits /search?type=roundTrip directly (deep link, agent test, partner referral),
  // route to the proper R/T page so the FE renders Onward + Return columns instead of a
  // confusing one-way list with R/T headers.
  useEffect(() => {
    const t = (searchParams.get('type') || searchParams.get('tripType') || '').toLowerCase();
    if (t === 'roundtrip' || t === 'round_trip' || t === 'round-trip') {
      const from = (searchParams.get('from') || '').toUpperCase();
      const to = (searchParams.get('to') || '').toUpperCase();
      const date = searchParams.get('date') || searchParams.get('departure') || '';
      const ret = searchParams.get('return') || '';
      if (!ret) return; // missing return date → fall through to one-way layout
      const adults = searchParams.get('adults') || '1';
      const children = searchParams.get('children') || '0';
      const infants = searchParams.get('infants') || '0';
      const cls = searchParams.get('class') || 'Economy';
      const isDomestic = DOMESTIC_AIRPORTS.includes(from) && DOMESTIC_AIRPORTS.includes(to);
      const target = isDomestic ? '/round-trip' : '/international-round-trip';
      const totalPax = Number(adults) + Number(children) + Number(infants);
      navigate(`${target}?from=${from}&to=${to}&date=${date}&return=${ret}&pax=${totalPax}&adults=${adults}&children=${children}&infants=${infants}&class=${cls}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search State
  const [showModifySearch, setShowModifySearch] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showTimingSheet, setShowTimingSheet] = useState(false);
  const [showSortSheet, setShowSortSheet] = useState(false);
  const [tripType, setTripType] = useState(searchParams.get('type') || searchParams.get('tripType') || 'oneWay');
  const [fromAirport, setFromAirport] = useState(searchParams.get('from') || 'BOM');
  const [toAirport, setToAirport] = useState(searchParams.get('to') || 'DXB');
  
  // Use future date by default (7 days from now)
  const getDefaultDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };
  
  const [departureDate, setDepartureDate] = useState(searchParams.get('date') || searchParams.get('departure') || getDefaultDate());
  const [returnDate, setReturnDate] = useState(searchParams.get('return') || '');
  const [adults, setAdults] = useState(Number(searchParams.get('adults')) || 1);
  const [children, setChildren] = useState(Number(searchParams.get('children')) || 0);
  const [infants, setInfants] = useState(Number(searchParams.get('infants')) || 0);
  const [travelClass, setTravelClass] = useState(searchParams.get('class') || 'Economy');

  // Real flight data from Tripjack
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [displayCount, setDisplayCount] = useState(20);
  const ITEMS_PER_PAGE = 20;
  
  // Sync state with URL params whenever they change
  useEffect(() => {
    const newTripType = searchParams.get('type') || searchParams.get('tripType') || 'oneWay';
    const newFrom = searchParams.get('from') || 'BOM';
    const newTo = searchParams.get('to') || 'DXB';
    const newDate = searchParams.get('date') || searchParams.get('departure') || getDefaultDate();
    const newReturn = searchParams.get('return') || '';
    const newAdults = Number(searchParams.get('adults')) || 1;
    const newChildren = Number(searchParams.get('children')) || 0;
    const newInfants = Number(searchParams.get('infants')) || 0;
    const newClass = searchParams.get('class') || 'Economy';
    
    // Only update if values actually changed
    if (newTripType !== tripType) setTripType(newTripType);
    if (newFrom !== fromAirport) setFromAirport(newFrom);
    if (newTo !== toAirport) setToAirport(newTo);
    if (newDate !== departureDate) setDepartureDate(newDate);
    if (newReturn !== returnDate) setReturnDate(newReturn);
    if (newAdults !== adults) setAdults(newAdults);
    if (newChildren !== children) setChildren(newChildren);
    if (newInfants !== infants) setInfants(newInfants);
    if (newClass !== travelClass) setTravelClass(newClass);
  }, [searchParams]);

  const fromCode = fromAirport;
  const toCode = toAirport;
  const date = departureDate;

  const [selectedStops, setSelectedStops] = useState([]);
  const [selectedAirlines, setSelectedAirlines] = useState([]);
  const [selectedDepartureTimes, setSelectedDepartureTimes] = useState([]);
  const [selectedArrivalTimes, setSelectedArrivalTimes] = useState([]);
  const [selectedAirports, setSelectedAirports] = useState([]);
  const [activeTab, setActiveTab] = useState({});  // Track active tab per flight
  const [fareRules, setFareRules] = useState({});  // keyed by `${flightId}__${priceId}` — per-fare rules cache
  const [loadingFareRules, setLoadingFareRules] = useState({});  // Loading state for fare rules
  const [selectedFares, setSelectedFares] = useState({}); // Track selected fare per flight

  // Cache key helper — rules are priceId-specific
  const fareRuleKey = (flightId, priceId) => `${flightId}__${priceId || ''}`;

  // Fetch fare rules for a specific (flight, priceId) combination. Cached per fare.
  // Sprint-5 · Supplier-aware: routes Riya rows through /api/riya/fare-rule,
  // offline rows return inline rules text, Tripjack uses /api/tripjack/fare-rules.
  const fetchFareRules = async (priceId, flightId, flight = null) => {
    const key = fareRuleKey(flightId, priceId);
    if (fareRules[key]) return; // Already loaded for this fare

    setLoadingFareRules(prev => ({ ...prev, [key]: true }));

    try {
      // Resolve flight from id if caller didn't pass it (back-compat)
      const fl = flight || flights.find(f => f.id === flightId) || null;
      const response = await fetchFareRulesForFlight(fl || { source: 'tripjack' }, priceId);
      if (response.data) {
        setFareRules(prev => ({ ...prev, [key]: response.data }));
      }
    } catch (error) {
      console.error('Error fetching fare rules:', error);
      setFareRules(prev => ({
        ...prev,
        [key]: { error: 'Fare rules are temporarily unavailable. Please check the airline website for cancellation and change policies.' }
      }));
    } finally {
      setLoadingFareRules(prev => ({ ...prev, [key]: false }));
    }
  };
  const [priceRange, setPriceRange] = useState([0, 200000]); // Wider range to show all flights
  const [sortBy, setSortBy] = useState('price');
  const [expandedFlight, setExpandedFlight] = useState(null);
  const [selectedDateIdx, setSelectedDateIdx] = useState(0);
  const [airlineLogos, setAirlineLogos] = useState({});

  // Transform Tripjack flight data to our format
  const transformTripjackFlights = (tripjackFlights) => {
    const transformedFlights = tripjackFlights.map((trip, index) => {
      const firstSegment = trip.sI?.[0] || {};
      const lastSegment = trip.sI?.[trip.sI.length - 1] || {};
      const firstFd = firstSegment.fD || {};

      // Wave-6 R4 · Codeshare (operating vs marketing carrier)
      const codeshare = deriveCodeshare(firstFd);

      // Get ALL fare options for this flight
      const fareOptions = (trip.totalPriceList || []).map(priceInfo => {
        const adultFare = priceInfo.fd?.ADULT || {};
        // Bug-fix May-2026 (errCode 1015): also expose CHILD/INFANT TFs
        // so BookingPage.computeFlightTotal sums per-pax fares correctly.
        // Pre-fix the transformer only read `fd.ADULT`, so for 1A+1C+1I
        // the FE total was `2 × adult_TF + 0` (infant fare missing).
        const childFare = priceInfo.fd?.CHILD || {};
        const infantFare = priceInfo.fd?.INFANT || {};

        // Sprint Feb-2026 · per-pax breakdown for the booking-time Fare
        // Summary card. `apply_markup_to_trip` writes `fc.MARKUP` and
        // `_original.{ADULT|CHILD|INFANT}` at /search-all so we can
        // surface a Service Charge line that's mathematically consistent
        // with the cart total. BF/TAF are supplier-net (no markup).
        const _aBf = Number(adultFare.fC?.BF || 0);
        const _aTaf = Number(adultFare.fC?.TAF || 0);
        const _aMu = Number(adultFare.fC?.MARKUP || 0);
        const _cBf = Number(childFare.fC?.BF || _aBf);
        const _cTaf = Number(childFare.fC?.TAF || _aTaf);
        const _cMu = Number(childFare.fC?.MARKUP || 0);
        const _iBf = Number(infantFare.fC?.BF || 0);
        const _iTaf = Number(infantFare.fC?.TAF || 0);
        const _iMu = Number(infantFare.fC?.MARKUP || 0);

        return {
          priceId: priceInfo.id,
          price: Math.round(adultFare.fC?.TF || 0),
          baseFare: Math.round(_aBf),
          taxes: Math.round(_aTaf),
          baggage: adultFare.bI?.iB || '15 Kg',
          cabinBaggage: adultFare.bI?.cB || '7 Kg',
          seatsLeft: adultFare.sR || null,
          mealIncluded: adultFare.mI || false,
          refundableType: adultFare.rT, // 0=Non-refundable, 1=Refundable, 2=Partially refundable
          fareType: publicFareTypeLabel(priceInfo.fareIdentifier),
          classOfBooking: adultFare.cB || 'Economy',
          // Per-pax-type fares for cart sum (PDF lines 595-621).
          fares: {
            adult: Math.round(adultFare.fC?.TF || 0),
            child: Math.round(childFare.fC?.TF || adultFare.fC?.TF || 0),
            infant: Math.round(infantFare.fC?.TF || 0),
          },
          // Sprint Feb-2026 · per-pax-type Base/Taxes/Markup breakdown.
          // Used by the Fare Summary "Service Charge" line. Values are
          // NUMBERS (not Math.rounded) so multi-pax aggregation in
          // BookingPage doesn't lose precision; the visible cells round
          // at render time via toLocaleString().
          fareBreakdown: {
            adult:  { bf: _aBf, taf: _aTaf, markup: _aMu },
            child:  { bf: _cBf, taf: _cTaf, markup: _cMu },
            infant: { bf: _iBf, taf: _iTaf, markup: _iMu },
          },
          tripBaggageBySegmentId: tripBaggageBySegmentFromPriceInfo(priceInfo) || undefined,
        };
      });

      // Use first (usually cheapest) fare option for main display
      const primaryFare = fareOptions[0] || {};

      // Calculate total duration
      const totalDuration = trip.sI?.reduce((sum, seg) => sum + (seg.duration || 0), 0) || 0;
      const hours = Math.floor(totalDuration / 60);
      const minutes = totalDuration % 60;

      return {
        id: primaryFare.priceId || `flight-${index}`,
        priceId: primaryFare.priceId,
        // ⚠️ source MUST be stamped — ReviewBooking.jsx checks this to
        // route to /api/tripjack/book instead of the offline path.
        source: 'tripjack',
        airline: firstSegment.fD?.aI?.name || 'Unknown Airline',
        airlineCode: firstSegment.fD?.aI?.code || 'XX',
        flightNumber: firstSegment.fD?.fN || 'N/A',
        departureTime: firstSegment.dt?.split('T')[1]?.substring(0, 5) || 'N/A',
        arrivalTime: lastSegment.at?.split('T')[1]?.substring(0, 5) || 'N/A',
        // Audit §7 L3 — `sI.Iand` next-day arrival flag (any segment lands +1d).
        nextDayArrival: Array.isArray(trip.sI)
          ? trip.sI.some((s) => Boolean(s?.Iand))
          : false,
        // Audit §7 L4 — Stopover airport IATA codes for connecting flights.
        stopAirports: Array.isArray(trip.sI) && trip.sI.length > 1
          ? trip.sI.slice(0, -1).map((s) => s?.aa?.code).filter(Boolean)
          : [],
        from: firstSegment.da?.code || fromAirport,
        to: lastSegment.aa?.code || toAirport,
        fromCity: firstSegment.da?.city || fromAirport,
        toCity: lastSegment.aa?.city || toAirport,
        stops: trip.sI?.length > 1 ? `${trip.sI.length - 1} Stop${trip.sI.length > 2 ? 's' : ''}` : 'Non Stop',
        duration: `${hours}h ${minutes}m`,
        price: primaryFare.price,
        originalPrice: primaryFare.price,
        baseFare: primaryFare.baseFare,
        taxes: primaryFare.taxes,
        baggage: primaryFare.baggage,
        cabinBaggage: primaryFare.cabinBaggage,
        seatsLeft: primaryFare.seatsLeft,
        mealIncluded: primaryFare.mealIncluded,
        refundableType: primaryFare.refundableType, // 0=Non-refundable, 1=Refundable, 2=Partially refundable
        fareType: primaryFare.fareType,
        // Bug-fix May-2026: expose per-pax-type TFs so BookingPage's
        // computeFlightTotal correctly sums `2A+1C+1I` etc.
        fares: primaryFare.fares,
        // Sprint Feb-2026 · per-pax-type fareBreakdown forwards to
        // BookingPage so the Fare Summary can aggregate Base/Taxes/Markup
        // correctly across multi-pax carts (replaces the legacy
        // single-adult `baseFare`/`taxes` fields above which were
        // displayed AS IF totals).
        fareBreakdown: primaryFare.fareBreakdown,
        tripBaggageBySegmentId: primaryFare.tripBaggageBySegmentId,
        fareOptions: fareOptions,  // All fare options for this flight
        // Wave-6 R4 · Codeshare (operated-by) info for FlightCard chip
        operatingAirline: codeshare.operatingAirline,
        operatingAirlineCode: codeshare.operatingAirlineCode,
        isCodeshare: codeshare.isCodeshare,
        segments: trip.sI || [],
        rawData: trip,  // Keep raw data for booking
        // Add unique flight key for grouping
        flightKey: `${firstSegment.fD?.aI?.code}-${firstSegment.fD?.fN}-${firstSegment.dt}-${lastSegment.at}`
      };
    });
    
    // Group flights by unique flight key (same physical flight, different fares).
    // Bug-fix Jun-2026: Tripjack returns the same physical flight multiple times
    // with DIFFERENT fare buckets (cheap promo + expensive flexi etc). The pre-fix
    // dedupe only swapped `price/priceId/id/baggage` to the cheaper fare and left
    // `baseFare/taxes/fareBreakdown/fares/...` pointing at the FIRST (often
    // expensive) bucket. That made the search card show ₹7,010 but the review/
    // booking page display ₹24,254 (the leftover expensive breakdown) — the
    // user clicked cheap but saw expensive on /booking. ALL display-derived
    // fields must follow the winning (cheapest) fare or the breakdown lies.
    const groupedFlights = {};
    transformedFlights.forEach(flight => {
      const key = flight.flightKey;
      if (!groupedFlights[key]) {
        groupedFlights[key] = flight;
      } else {
        // Merge fare options from duplicate flight entries
        groupedFlights[key].fareOptions = [
          ...groupedFlights[key].fareOptions,
          ...flight.fareOptions
        ];
        // Use the cheapest price as primary — copy ALL fare-derived display
        // fields, not just the headline price, otherwise the review page
        // renders the wrong bucket's baseFare / taxes / fareBreakdown.
        if (flight.price < groupedFlights[key].price) {
          groupedFlights[key].price = flight.price;
          groupedFlights[key].originalPrice = flight.originalPrice;
          groupedFlights[key].priceId = flight.priceId;
          groupedFlights[key].id = flight.id;
          groupedFlights[key].baseFare = flight.baseFare;
          groupedFlights[key].taxes = flight.taxes;
          groupedFlights[key].fareBreakdown = flight.fareBreakdown;
          groupedFlights[key].fares = flight.fares;
          groupedFlights[key].baggage = flight.baggage;
          groupedFlights[key].cabinBaggage = flight.cabinBaggage;
          groupedFlights[key].tripBaggageBySegmentId = flight.tripBaggageBySegmentId;
          groupedFlights[key].seatsLeft = flight.seatsLeft;
          groupedFlights[key].mealIncluded = flight.mealIncluded;
          groupedFlights[key].refundableType = flight.refundableType;
          groupedFlights[key].fareType = flight.fareType;
          groupedFlights[key].rawData = flight.rawData;
        }
      }
    });
    
    return Object.values(groupedFlights);
  };

  // Performance: Fetch real flights from Tripjack on mount
  // Fetch flights function
  const fetchFlights = async () => {
    setLoading(true);
    setError(null);

    // Analytics: one search event per fetch (covers initial load + any param changes)
    trackSearch({
      from: fromAirport,
      to: toAirport,
      date: departureDate,
      return_date: returnDate || null,
      trip_type: tripType === 'roundTrip' ? 'roundtrip' : 'oneway',
      adults,
      children,
      infants,
      class: travelClass,
    });

    try {
      console.log('Fetching flights from unified search...', {
        origin: fromAirport,
        destination: toAirport,
        departure_date: departureDate
      });

      // Sprint-6 · Forward industry-form params from URL to /search-all so
      // pft / preferred_airlines / direct_only / connecting_only set on HomePage actually
      // reach the supplier (B3/B4/B5).
      const _pft = searchParams.get('pft') || undefined;
      const _preferred = searchParams.get('preferred_airlines') || undefined;
      const _directOnly = searchParams.get('direct_only') === '1';
      const _connectingOnly = searchParams.get('connecting_only') === '1';

      // Call unified search endpoint (Tripjack + Offline inventory)
      const response = await axios.get(`${API}/flights/search-all`, {
        params: {
          from_airport: fromAirport,
          to_airport: toAirport,
          date: departureDate,
          adults: adults,
          children: children,
          infants: infants,
          class_type: travelClass,
          include_tripjack: true,
          include_offline: true,
          ...(_pft ? { pft: _pft } : {}),
          ...(_preferred ? { preferred_airlines: _preferred } : {}),
          ...(_directOnly ? { direct_only: true } : {}),
          ...(_connectingOnly ? { connecting_only: true } : {}),
          // Mini-Sprint 1 · markup context: agent session wins over default b2c.
          user_type: agentSession?.id ? 'agent' : 'b2c',
          ...(agentSession?.id ? { agent_id: agentSession.id } : {}),
        }
      });

      console.log('Unified search API response:', response.data);

      if (response.data?.success && response.data?.combined_flights) {
        const allFlights = response.data.combined_flights;
        
        // Separate by source: Tripjack (raw shape), Riya (normalised), Offline
        const tripjackFlights = allFlights.filter(f => f.source === "tripjack" || (!f.source && f.sI));
        const riyaFlights = allFlights.filter(f => f.source === "riya");
        const offlineFlights = allFlights.filter(f => f.source === "offline");
        
        console.log(`Tripjack: ${tripjackFlights.length} | Riya: ${riyaFlights.length} | Offline: ${offlineFlights.length}`);
        
        // Transform each family into the common card shape
        const transformedTripjack = tripjackFlights.length > 0 
          ? transformTripjackFlights(tripjackFlights)
          : [];
        
        const adaptedRiya = riyaFlights.map((r, i) => {
          // Synthesize a minimal segment array so downstream rendering
          // (Journey tab timeline, <FlightDetailsCard>, etc.) works uniformly
          // across suppliers. Riya returns flight data at the top level only.
          const synthSegments = Array.isArray(r.segments) && r.segments.length > 0
            ? r.segments
            : [{
                id: r.id || `riya-seg-${i}`,
                dt: r.departureTime,  // "YYYY-MM-DDTHH:mm"
                at: r.arrivalTime,
                duration: r.durationMinutes || 0,
                fD: {
                  aI: { name: r.airline, code: r.airlineCode },
                  fN: r.flightNumber,
                  eT: r.aircraft || '',
                },
                da: { code: r.from, city: r.fromCity || r.from, terminal: r.fromTerminal || '' },
                aa: { code: r.to, city: r.toCity || r.to, terminal: r.toTerminal || '' },
              }];

          return {
            id: r.id || r.priceId || `riya-${i}`,
            priceId: r.priceId || r.id,
            airline: r.airline || 'Airline',
            airlineCode: r.airlineCode || 'XX',
            flightNumber: r.flightNumber || 'N/A',
            departureTime: (r.departureTime || '').split('T')[1]?.substring(0, 5) || 'N/A',
            arrivalTime: (r.arrivalTime || '').split('T')[1]?.substring(0, 5) || 'N/A',
            from: r.from,
            to: r.to,
            fromCity: r.fromCity || r.from,
            toCity: r.toCity || r.to,
            stops: r.stops || 'Non Stop',
            duration: r.duration,
            price: r.price,
            originalPrice: r.price,
            baseFare: r.baseFare,
            taxes: r.tax,
            baggage: r.baggage || '15 Kg',
            cabinBaggage: r.cabinBaggage || '7 Kg',
            seatsLeft: r.seatsLeft,
            mealIncluded: !!r.mealIncluded,
            refundableType: r.refundable ? 1 : 0,
            fareType: r.fareType || 'Standard',
            classOfBooking: r.classOfBooking || 'Economy',
            fareOptions: [],  // Riya pricing happens at Book-time, not here
            segments: synthSegments,
            source: 'riya',
            // Riya-specific fields needed for the booking flow (Pricing → Book → IssueTicket).
            // These MUST flow through to localStorage.selectedFlight or the backend will
            // reject /Pricing with "(&8) TrackId tag is empty".
            riyaTrackId: r.riyaTrackId,
            riyaFlightId: r.riyaFlightId,
            riyaFlightIds: r.riyaFlightIds,
            riyaBaseAmount: r.riyaBaseAmount,
            riyaGrossAmount: r.riyaGrossAmount,
            riyaSearchedAt: r.riyaSearchedAt,
            riyaItinRef: r.riyaItinRef,
            riyaSegRef: r.riyaSegRef,
            riyaReferenceToken: r.riyaReferenceToken,
            riyaAllowBlockPNR: r.riyaAllowBlockPNR,
            riyaAirlineCategory: r.riyaAirlineCategory,
            riyaSegmentType: r.riyaSegmentType,
            segmentType: r.segmentType,
            riyaCombo: r.riyaCombo,
            riyaComboGroupId: r.riyaComboGroupId,
            fares: r.fares,
            riyaRaw: r,
          };
        });
        
        const enrichedOffline = offlineFlights.map(enrichOfflineFlight);
        
        const combinedTransformed = [...transformedTripjack, ...adaptedRiya, ...enrichedOffline];
        
        console.log(`Total: ${combinedTransformed.length} (${transformedTripjack.length} Tripjack + ${adaptedRiya.length} Riya + ${enrichedOffline.length} Offline)`);
        setFlights(combinedTransformed);
      } else {
        console.log('No flights in response');
        setFlights([]);
      }
    } catch (err) {
      console.error('Flight search error:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to load flights. Please try again.');
      setFlights([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch flights when search parameters change
  useEffect(() => {
    fetchFlights();
  }, [fromAirport, toAirport, departureDate, returnDate, adults, children, infants, travelClass, searchParams]);

  const fromAirportInfo = airports.find((a) => a.code === fromCode);
  const toAirportInfo = airports.find((a) => a.code === toCode);

  // Extract unique airlines from flight results (dynamic)
  const uniqueAirlines = useMemo(() => {
    const airlineMap = new Map();
    flights.forEach(flight => {
      if (!airlineMap.has(flight.airlineCode)) {
        airlineMap.set(flight.airlineCode, {
          code: flight.airlineCode,
          name: flight.airline,
          count: 1
        });
      } else {
        airlineMap.get(flight.airlineCode).count++;
      }
    });
    return Array.from(airlineMap.values());
  }, [flights]);

  const uniqueAirports = useMemo(() => {
    const airportMap = new Map();
    flights.forEach(flight => {
      if (!airportMap.has(flight.from)) {
        airportMap.set(flight.from, {
          code: flight.from,
          city: flight.fromCity
        });
      }
      if (!airportMap.has(flight.to)) {
        airportMap.set(flight.to, {
          code: flight.to,
          city: flight.toCity
        });
      }
    });
    return Array.from(airportMap.values());
  }, [flights]);

  // Calculate cheapest price from actual flights (dynamic)
  const cheapestPrice = useMemo(() => {
    if (flights.length === 0) return 0;
    return Math.min(...flights.map(f => f.price));
  }, [flights]);

  // Calculate stop counts from actual flights (dynamic)
  const stopCounts = useMemo(() => {
    return {
      nonStop: flights.filter(f => f.stops === 'Non Stop').length,
      oneStop: flights.filter(f => f.stops === '1 Stop').length,
      twoPlus: flights.filter(f => f.stops.includes('2') || f.stops.includes('3')).length
    };
  }, [flights]);

  // Fetch airline tail logos (Logostream, cached via utils/airlineLogoCache)
  useEffect(() => {
    if (uniqueAirlines.length > 0) {
      const codes = uniqueAirlines.map(a => a.code);
      fetchAirlineLogos(codes)
        .then(data => setAirlineLogos(prev => ({ ...prev, ...data })))
        .catch(err => console.error('Airline logo fetch error:', err));
    }
  }, [uniqueAirlines]);

  // Helper function to check if a time falls in a time slot
  const isTimeInSlot = (time, slotId) => {
    const hour = parseInt(time.split(':')[0]);
    if (slotId === 'earlyMorning') return hour >= 0 && hour < 6;
    if (slotId === 'morning') return hour >= 6 && hour < 12;
    if (slotId === 'afternoon') return hour >= 12 && hour < 18;
    if (slotId === 'evening') return hour >= 18 && hour < 24;
    return false;
  };

  const filteredFlights = useMemo(() => {
    let filteredList = [...flights];
    
    // Filter by stops
    if (selectedStops.length > 0) {
      filteredList = filteredList.filter((f) => {
        if (selectedStops.includes('nonStop') && f.stops === 'Non Stop') return true;
        if (selectedStops.includes('1stop') && f.stops.includes('1 Stop')) return true;
        if (selectedStops.includes('2stop') && f.stops.includes('2 Stop')) return true;
        return false;
      });
    }
    
    // Filter by airlines
    if (selectedAirlines.length > 0) {
      filteredList = filteredList.filter((f) => selectedAirlines.includes(f.airlineCode));
    }

    // Filter by departure time
    if (selectedDepartureTimes.length > 0) {
      filteredList = filteredList.filter((f) => 
        selectedDepartureTimes.some(slot => isTimeInSlot(f.departureTime, slot))
      );
    }

    // Filter by arrival time
    if (selectedArrivalTimes.length > 0) {
      filteredList = filteredList.filter((f) => 
        selectedArrivalTimes.some(slot => isTimeInSlot(f.arrivalTime, slot))
      );
    }

    // Filter by airports (from or to)
    if (selectedAirports.length > 0) {
      filteredList = filteredList.filter((f) => 
        selectedAirports.includes(f.from) || selectedAirports.includes(f.to)
      );
    }
    
    // Filter by price range
    filteredList = filteredList.filter((f) => f.price >= priceRange[0] && f.price <= priceRange[1]);
    
    // Sort
    if (sortBy === 'price') filteredList.sort((a, b) => a.price - b.price);
    if (sortBy === 'duration') filteredList.sort((a, b) => {
      const [aH, aM] = a.duration.split('h ').map(s => parseInt(s));
      const [bH, bM] = b.duration.split('h ').map(s => parseInt(s));
      return (aH * 60 + aM) - (bH * 60 + bM);
    });
    if (sortBy === 'departure') filteredList.sort((a, b) => a.departureTime.localeCompare(b.departureTime));
    
    return filteredList;
  }, [flights, selectedStops, selectedAirlines, selectedDepartureTimes, selectedArrivalTimes, selectedAirports, priceRange, sortBy]);

  // Paginated flights - only show limited number for performance
  const paginatedFlights = useMemo(() => {
    return filteredFlights.slice(0, displayCount);
  }, [filteredFlights, displayCount]);

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + ITEMS_PER_PAGE);
  };

  // Auto-load more when the sentinel scrolls into view (infinite scroll)
  const hasMoreFlights = displayCount < filteredFlights.length;
  const infiniteScrollRefMobile = useInfiniteScroll({
    hasMore: hasMoreFlights && !loading && !error,
    onLoadMore: handleLoadMore,
  });
  const infiniteScrollRefDesktop = useInfiniteScroll({
    hasMore: hasMoreFlights && !loading && !error,
    onLoadMore: handleLoadMore,
  });

  const toggleStop = (stop) => setSelectedStops((p) => p.includes(stop) ? p.filter((s) => s !== stop) : [...p, stop]);
  const toggleAirline = (code) => setSelectedAirlines((p) => p.includes(code) ? p.filter((c) => c !== code) : [...p, code]);

  const airlineColors = {
    'IX': '#F26522', 'SG': '#DC2626', 'G9': '#E53935',
    '6E': '#1A237E', 'AI': '#DC2626', 'EK': '#C62828',
    'EY': '#5C4033', 'IX2': '#DC2626', 'MS': '#1565C0',
  };

  // Handle Modify Search
  const handleModifySearch = (values) => {
    // Show loading state immediately
    setLoading(true);
    setError(null);
    setFlights([]);
    
    // If values are passed, use them (from DesktopModifySearch)
    // Otherwise use current state (from mobile modify)
    if (values) {
      const totalPax = values.adults + values.children + values.infants;
      navigate(`/search?from=${values.fromAirport}&to=${values.toAirport}&date=${values.departureDate}&return=${values.returnDate}&pax=${totalPax}&adults=${values.adults}&children=${values.children}&infants=${values.infants}&class=${values.travelClass}&type=${values.tripType}`);
    } else {
      const totalPax = adults + children + infants;
      navigate(`/search?from=${fromAirport}&to=${toAirport}&date=${departureDate}&return=${returnDate}&pax=${totalPax}&adults=${adults}&children=${children}&infants=${infants}&class=${travelClass}&type=${tripType}`);
    }
    setShowModifySearch(false);
  };

  const handleSwapAirports = () => {
    const tmp = fromAirport;
    setFromAirport(toAirport);
    setToAirport(tmp);
  };


  const handleRefresh = async () => {
    // Simulate refresh - in real app, refetch data
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  return (
    <div className="min-h-screen travel-bg pb-20 md:pb-0">
      <PullToRefresh onRefresh={handleRefresh}>
      {/* ══════════ MOBILE VERSION ══════════ */}
      <div className="md:hidden">
        {/* Mobile Header with Back Button */}
        <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 p-3">
            <button
              onClick={() => navigate('/')}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 transition-colors"
              aria-label="Back to home"
            >
              <ChevronLeft className="w-5 h-5 text-[#1B375B]" />
            </button>
            
            <button
              onClick={() => setShowModifySearch(true)}
              className="flex-1 bg-gray-100 rounded-full px-4 py-3 active:bg-gray-200 transition-colors"
              style={{ minHeight: '48px' }}
            >
              <div className="text-center">
                <p className="font-bold text-[#1B375B] text-base">
                  {fromCode} → {toCode}
                </p>
                <p className="text-xs text-gray-500">
                  {departureDate ? new Date(departureDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) : 'Date'} • {adults + children + infants} Traveller • {travelClass}
                </p>
              </div>
            </button>

            <button className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200 transition-colors">
              <Users className="w-5 h-5 text-[#1B375B]" />
            </button>
          </div>
        </div>

        {/* Horizontal Date Selector - Removed (use dynamic data when implementing date search) */}

        {/* Flight Cards - Ixigo Style - Separated Cards */}
        <div className="p-3 space-y-3 pb-24">
          {/* Loading State with Skeleton */}
          {loading && (
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <FlightSkeleton key={i} />
              ))}
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-medium mb-2">{error}</p>
              <button
                onClick={fetchFlights}
                className="mt-3 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          )}

          {/* No Flights Found */}
          {!loading && !error && filteredFlights.length === 0 && (
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <Plane className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No flights found</p>
              <p className="text-sm text-gray-500 mt-1">Try adjusting your search criteria</p>
            </div>
          )}

          {/* Flight Results - Mobile Optimized */}
          {!loading && !error && paginatedFlights.map((flight) => {
            const isSoldOut = flight.isSoldOut || (selectedFares[flight.id]?.seatsLeft ?? flight.seatsLeft) === 0;
            return (
            <div
              key={flight.id}
              className={`bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden ${isSoldOut ? 'opacity-60' : ''}`}
            >
              <div className="p-2.5 sm:p-4">
                {/* Header: Airline + Fare Type Badge + Offline Badge */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-[#1B375B]">
                      {flight.airline} • {flight.flightNumber}
                    </p>
                    {/* Sold Out Badge */}
                    {isSoldOut && (
                      <span data-testid="flight-mobile-sold-out-badge" className="px-2 py-1 text-xs font-bold rounded-full bg-gray-700 text-white">
                        SOLD OUT
                      </span>
                    )}
                    {/* Offline/Block Ticket Badge */}
                    {flight.source === "offline" && (
                      <span className="px-2 py-1 text-xs font-bold rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                        🎫 Block Ticket
                      </span>
                    )}
                  </div>
                  {(selectedFares[flight.id]?.fareType || flight.fareType) && (() => {
                    const currentFareType = selectedFares[flight.id]?.fareType || flight.fareType;
                    const fareInfo = getFareTypeInfo(currentFareType);
                    return (
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${fareInfo.bgColor} ${fareInfo.textColor}`}>
                        {fareInfo.label}
                      </span>
                    );
                  })()}
                </div>

                <div className="flex items-start gap-2 sm:gap-3">
                  {/* Airline Tail Logo — no container, just the tail graphic */}
                  <div className="flex-shrink-0">
                    {airlineLogos[flight.airlineCode]?.square ? (
                      <img 
                        src={airlineLogos[flight.airlineCode].square} 
                        alt={flight.airline} 
                        className="w-8 h-8 sm:w-10 sm:h-10 object-contain" 
                      />
                    ) : (
                      <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center">
                        <Plane className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Flight Details - Responsive for all mobile screens */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-1.5 sm:gap-2">
                      {/* Departure */}
                      <div className="flex-shrink-0 min-w-0">
                        <p className="text-base sm:text-xl font-bold text-[#1B375B] leading-tight">{flight.departureTime}</p>
                        <p className="text-[10px] sm:text-xs font-semibold text-gray-700 mt-0.5 truncate">{flight.from}</p>
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">{flight.fromCity}</p>
                      </div>
                      
                      {/* Duration + Stops */}
                      <div className="flex-shrink-0 px-1.5 sm:px-2 text-center min-w-[60px] sm:min-w-[70px]">
                        <p className="text-[10px] sm:text-xs text-gray-500">{flight.duration}</p>
                        <div className="h-px bg-gray-300 my-1" />
                        <p className="text-[10px] sm:text-xs font-medium text-gray-600">{flight.stops}</p>
                      </div>
                      
                      {/* Arrival */}
                      <div className="text-right flex-shrink-0 min-w-0">
                        <p className="text-base sm:text-xl font-bold text-[#1B375B] leading-tight">{flight.arrivalTime}</p>
                        <p className="text-[10px] sm:text-xs font-semibold text-gray-700 mt-0.5 truncate">{flight.to}</p>
                        <p className="text-[10px] sm:text-xs text-gray-500 truncate">{flight.toCity}</p>
                      </div>
                    </div>

                    {/* Price Row - Moved below for better small screen layout */}
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        {!isSoldOut && (selectedFares[flight.id]?.price || flight.price) === cheapestPrice && (
                          <span className="bg-green-50 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded border border-green-200">
                            CHEAPEST
                          </span>
                        )}
                      </div>
                      <p className="text-lg sm:text-xl font-bold text-[#1B375B]">
                        ₹{(selectedFares[flight.id]?.price || flight.price)?.toLocaleString() || '0'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bottom Info Bar - Mobile Responsive */}
                {/* Bug-fix Feb-2026 · Chips now read from the user's
                    selected fare (selectedFares[flight.id]) — pre-fix they
                    read `flight.*` directly so baggage/meal/seats/refund
                    stayed frozen on the cheapest fare values even when
                    the user picked a different fare card above. */}
                {(() => {
                  const _sel = selectedFares[flight.id] || {};
                  const chipBaggage = _sel.baggage ?? flight.baggage;
                  const chipCabinBag = _sel.cabinBaggage ?? flight.cabinBaggage;
                  const chipMealIncluded = _sel.mealIncluded ?? flight.mealIncluded;
                  const chipSeatsLeft = _sel.seatsLeft ?? flight.seatsLeft;
                  const chipRefundType = _sel.refundableType ?? flight.refundableType;
                  return (
                <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-[10px] sm:text-xs mb-2">
                    {/* Baggage */}
                    <div className="flex items-center gap-1 text-gray-600">
                      <Luggage size={12} className="sm:w-3.5 sm:h-3.5" />
                      <span>{chipCabinBag} + {chipBaggage}</span>
                    </div>
                    
                    {/* Meal Included */}
                    {chipMealIncluded && (
                      <div className="flex items-center gap-1 text-green-600 font-medium">
                        <Utensils size={12} className="sm:w-3.5 sm:h-3.5" />
                        <span>Meal</span>
                      </div>
                    )}
                    
                    {/* Seats Left */}
                    {chipSeatsLeft && chipSeatsLeft <= 10 && (
                      <div className="flex items-center gap-1 text-orange-600 font-semibold">
                        <AlertCircle size={12} className="sm:w-3.5 sm:h-3.5" />
                        <span>{chipSeatsLeft} left</span>
                      </div>
                    )}

                    {/* Refundability Badge */}
                    {chipRefundType !== undefined && (() => {
                      const refundInfo = getRefundabilityInfo(chipRefundType);
                      return (
                        <span className={`px-2 py-0.5 ${refundInfo.bgColor} ${refundInfo.textColor} text-xs font-semibold rounded border ${refundInfo.borderColor}`}>
                          {refundInfo.label}
                        </span>
                      );
                    })()}
                  </div>

                  <div className="flex items-center justify-between">
                    {/* Flight Details Toggle */}
                    <button
                      onClick={() => setExpandedFlight(expandedFlight === flight.id ? null : flight.id)}
                      className="flex items-center gap-1 text-xs text-teal-600 font-semibold"
                      style={{ minHeight: '36px', minWidth: '44px' }}
                    >
                      Flight Details
                      {expandedFlight === flight.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {/* Book Button */}
                    <button
                      type="button"
                      data-testid={`flight-book-btn-${flight.source || 'tripjack'}-${(selectedFares[flight.id] || flight).priceId || flight.id}`}
                      disabled={isSoldOut}
                      onClick={() => {
                        if (isSoldOut) return;
                        // Build the localStorage payload via the shared helper
                        // so every Book button across the codebase carries the
                        // SAME field-override contract (price, fares, fareBreakdown,
                        // baggage, mealIncluded, refundableType, ...). See
                        // utils/fareSelection.js + tests for the contract.
                        const fareToBook = selectedFares[flight.id] || flight;
                        const flightDataToSave = buildSelectedFlightPayload(flight, fareToBook);
                        localStorage.setItem('selectedFlight', JSON.stringify(flightDataToSave));
                        localStorage.setItem('searchTimestamp', Date.now().toString());
                        navigate(`/booking?priceId=${fareToBook.priceId}&type=oneway&adults=${adults}&children=${children}&infants=${infants}`);
                      }}
                      className={`px-4 py-1.5 font-semibold rounded-lg transition-colors ${
                        isSoldOut
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-teal-600 hover:bg-teal-700 text-white'
                      }`}
                      style={{ minHeight: '36px', minWidth: '44px' }}
                    >
                      {isSoldOut ? 'Sold Out' : 'Book'}
                    </button>
                  </div>
                </div>
                  );
                })()}
              </div>

              {/* Expanded Flight Details - Mobile */}
              {expandedFlight === flight.id && (
                <div className="bg-gray-50 border-t border-gray-200">
                  {/* Tabs - Mobile */}
                  <div className="flex border-b border-gray-300 bg-white">
                    <button
                      onClick={() => setActiveTab(prev => ({ ...prev, [flight.id]: 'journey' }))}
                      className={`flex-1 px-3 py-2.5 text-xs font-semibold transition-colors relative ${
                        (activeTab[flight.id] || 'journey') === 'journey'
                          ? 'text-[#1B375B]'
                          : 'text-gray-400'
                      }`}
                    >
                      Flight Journey
                      {(activeTab[flight.id] || 'journey') === 'journey' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#659A9B]" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setActiveTab(prev => ({ ...prev, [flight.id]: 'fareRules' }));
                        const activePid = selectedFares[flight.id]?.priceId || flight.priceId;
                        fetchFareRules(activePid, flight.id);
                      }}
                      className={`flex-1 px-3 py-2.5 text-xs font-semibold transition-colors relative ${
                        activeTab[flight.id] === 'fareRules'
                          ? 'text-[#1B375B]'
                          : 'text-gray-400'
                      }`}
                    >
                      Fare Rules
                      {activeTab[flight.id] === 'fareRules' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#659A9B]" />
                      )}
                    </button>
                  </div>

                  {/* Tab Content - Mobile */}
                  <div className="px-4 py-3">
                    {/* Journey Tab */}
                    {(activeTab[flight.id] || 'journey') === 'journey' && (
                      <div>
                        {/* Flight Journey Timeline */}
                        <div className="space-y-3">
                    {(flight.segments || []).map((segment, idx) => {
                      const departureTime = segment.dt?.split('T')[1]?.substring(0, 5) || 'N/A';
                      const arrivalTime = segment.at?.split('T')[1]?.substring(0, 5) || 'N/A';
                      const departureDate = segment.dt?.split('T')[0] || '';
                      const arrivalDate = segment.at?.split('T')[0] || '';
                      const segmentDuration = segment.duration || 0;
                      const segmentHours = Math.floor(segmentDuration / 60);
                      const segmentMinutes = segmentDuration % 60;
                      const nextSegment = (flight.segments || [])[idx + 1];
                      
                      return (
                        <div key={segment.id || idx}>
                          {/* Segment Card */}
                          <div className="bg-white rounded-lg border border-gray-200 p-3">
                            {/* Airline & Flight Info */}
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-bold text-[#1B375B]">
                                {segment.fD?.aI?.name || 'Airline'} {segment.fD?.fN || 'N/A'}
                              </span>
                              {segment.fD?.eT && (
                                <span className="text-[9px] bg-gray-100 text-gray-600 font-medium px-1.5 py-0.5 rounded">
                                  Aircraft: {segment.fD.eT}
                                </span>
                              )}
                            </div>

                            {/* Route */}
                            <div className="flex items-center gap-2">
                              {/* Departure */}
                              <div className="flex-1">
                                <p className="text-lg font-bold text-[#1B375B]">{departureTime}</p>
                                <p className="text-xs font-semibold text-gray-700">
                                  {segment.da?.city || 'City'} ({segment.da?.code || 'XXX'})
                                </p>
                                {segment.da?.terminal && (
                                  <p className="text-[9px] text-gray-500">{segment.da.terminal}</p>
                                )}
                              </div>

                              {/* Duration */}
                              <div className="flex flex-col items-center px-2">
                                <Clock className="w-3 h-3 text-teal-600 mb-1" />
                                <span className="text-[10px] font-semibold text-gray-600">
                                  {segmentHours}h {segmentMinutes}m
                                </span>
                              </div>

                              {/* Arrival */}
                              <div className="flex-1 text-right">
                                <p className="text-lg font-bold text-[#1B375B]">{arrivalTime}</p>
                                <p className="text-xs font-semibold text-gray-700">
                                  {segment.aa?.city || 'City'} ({segment.aa?.code || 'XXX'})
                                </p>
                                {segment.aa?.terminal && (
                                  <p className="text-[9px] text-gray-500">{segment.aa.terminal}</p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Layover Info */}
                          {nextSegment && segment.cT && (
                            <div className="flex items-center gap-1.5 py-1.5 px-2">
                              <Clock className="w-3 h-3 text-orange-500" />
                              <span className="text-[10px] text-orange-600 font-semibold">
                                Layover: {Math.floor(segment.cT / 60)}h {segment.cT % 60}m in {segment.aa?.city || 'Transit'}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Summary */}
                  <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <p className="text-gray-500 mb-0.5">Total Duration</p>
                      <p className="font-bold text-[#1B375B]">{flight.duration}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-0.5">Stops</p>
                      <p className="font-bold text-[#1B375B]">{flight.stops}</p>
                    </div>
                  </div>
                  
                  {/* Multiple Fare Options - Mobile */}
                  {flight.fareOptions && flight.fareOptions.length > 1 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-3">
                        {flight.fareOptions.length} fare options - Select one:
                      </p>
                      <div className="grid grid-cols-1 gap-2">
                        {flight.fareOptions.map((fare, idx) => {
                          const isSelected = selectedFares[flight.id]?.priceId === fare.priceId;
                          return (
                            <button
                              key={idx}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedFares(prev => ({
                                  ...prev,
                                  [flight.id]: fare
                                }));
                                // If Fare Rules tab is currently visible, refetch for the newly-selected fare
                                if (activeTab[flight.id] === 'fareRules' && fare.priceId) {
                                  fetchFareRules(fare.priceId, flight.id);
                                }
                              }}
                              className={`flex items-center justify-between p-3 border-2 rounded-lg transition-all ${
                                isSelected
                                  ? 'border-teal-500 bg-teal-50 shadow-md'
                                  : 'bg-white border-gray-200 active:border-teal-300'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {isSelected && (
                                  <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                )}
                                <div className="text-left">
                                  <p className="text-sm font-bold text-gray-800">
                                    {getFareTypeInfo(fare.fareType).label}
                                  </p>
                                  <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                                    <Luggage size={12} />
                                    <span>{fare.baggage}</span>
                                  </div>
                                </div>
                              </div>
                              <p className={`text-lg font-bold ${isSelected ? 'text-teal-600' : 'text-gray-700'}`}>
                                ₹{fare.price.toLocaleString()}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                      </div>
                    )}

                    {/* Fare Rules Tab - Mobile */}
                    {activeTab[flight.id] === 'fareRules' && (
                      <div>
                        {loadingFareRules[fareRuleKey(flight.id, selectedFares[flight.id]?.priceId || flight.priceId)] ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                            <span className="ml-2 text-xs text-gray-500">Loading...</span>
                          </div>
                        ) : fareRules[fareRuleKey(flight.id, selectedFares[flight.id]?.priceId || flight.priceId)]?.error ? (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                            <p className="text-xs text-red-600">Fare rules are temporarily unavailable. Please check the airline website for cancellation and change policies.</p>
                          </div>
                        ) : (
                          <FareRulesCompact
                            normalized={fareRules[fareRuleKey(flight.id, selectedFares[flight.id]?.priceId || flight.priceId)]?._normalized}
                            compact="default"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Multiple Fare Options */}
              {flight.fareOptions && flight.fareOptions.length > 1 && (
                <div className="bg-gray-50 px-4 py-2 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">
                    {flight.fareOptions.length} fare options available - Select one:
                  </p>
                  <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                    {flight.fareOptions.map((fare, idx) => {
                      const isSelected = selectedFares[flight.id]?.priceId === fare.priceId;
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            // Select this fare for this flight
                            setSelectedFares(prev => ({
                              ...prev,
                              [flight.id]: fare
                            }));
                            // Refetch fare rules if that tab is open
                            if (activeTab[flight.id] === 'fareRules' && fare.priceId) {
                              fetchFareRules(fare.priceId, flight.id);
                            }
                          }}
                          className={`flex-shrink-0 px-3 py-2 border-2 rounded-lg transition-all ${
                            isSelected
                              ? 'border-teal-500 bg-teal-50 shadow-md'
                              : 'bg-white border-gray-200 hover:border-teal-300 hover:bg-teal-50/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-xs font-semibold text-gray-700">
                              {getFareTypeInfo(fare.fareType).label}
                            </p>
                            {isSelected && (
                              <div className="w-4 h-4 bg-teal-500 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <p className={`text-sm font-bold ${isSelected ? 'text-teal-600' : 'text-gray-700'}`}>
                            ₹{fare.price.toLocaleString()}
                          </p>
                          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                            <Luggage size={12} />
                            <span>{fare.baggage}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>

        {/* ── Mobile Load More + Infinite Scroll Sentinel ── */}
        {!loading && !error && hasMoreFlights && (
          <div className="px-4 mt-4 mb-24 flex flex-col items-center gap-3">
            <button
              onClick={handleLoadMore}
              className="w-full max-w-sm px-6 py-3 bg-white border-2 border-[#659A9B] text-[#1B375B] rounded-xl font-bold active:bg-[#659A9B] active:text-white transition-all shadow-sm flex items-center justify-center gap-2"
              data-testid="load-more-flights-mobile-btn"
            >
              <span>Load More Flights</span>
              <span className="bg-[#659A9B] text-white px-2 py-0.5 rounded-full text-xs">
                +{filteredFlights.length - displayCount}
              </span>
            </button>
            <div ref={infiniteScrollRefMobile} className="h-1 w-full" aria-hidden="true" />
            <p className="text-xs text-gray-400 animate-pulse">Loading more as you scroll...</p>
          </div>
        )}

        {/* Bottom Sheets */}
        {/* Mobile: Bottom Sheet for Modify Search - Temporarily disabled (using desktop version for all devices) */}


        {/* Mobile Filter Sheet */}
        <MobileFilterSheet
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          selectedStops={selectedStops}
          setSelectedStops={setSelectedStops}
          selectedAirlines={selectedAirlines}
          setSelectedAirlines={setSelectedAirlines}
          priceRange={priceRange}
          setPriceRange={setPriceRange}
          stopCounts={stopCounts}
          uniqueAirlines={uniqueAirlines}
          airlineLogos={airlineLogos}
        />

        {/* Mobile Modify Search Sheet - Shows on mobile, hidden on desktop */}
        <MobileModifySearch
          isOpen={showModifySearch}
          onClose={() => setShowModifySearch(false)}
          tripType={tripType}
          fromAirport={fromAirport}
          setFromAirport={setFromAirport}
          toAirport={toAirport}
          setToAirport={setToAirport}
          departureDate={departureDate}
          setDepartureDate={setDepartureDate}
          returnDate={returnDate}
          setReturnDate={setReturnDate}
          adults={adults}
          setAdults={setAdults}
          children={children}
          setChildren={setChildren}
          infants={infants}
          setInfants={setInfants}
          travelClass={travelClass}
          setTravelClass={setTravelClass}
          onSearch={handleModifySearch}
        />

      </div>

      {/* ══════════ DESKTOP VERSION ══════════ */}
      <div className="hidden md:block">

      {/* ── Desktop Modify Search Component ── */}
      <DesktopModifySearch
        showModifySearch={showModifySearch}
        setShowModifySearch={setShowModifySearch}
        tripType={tripType}
        setTripType={setTripType}
        fromAirport={fromAirport}
        setFromAirport={setFromAirport}
        toAirport={toAirport}
        setToAirport={setToAirport}
        departureDate={departureDate}
        setDepartureDate={setDepartureDate}
        returnDate={returnDate}
        setReturnDate={setReturnDate}
        adults={adults}
        setAdults={setAdults}
        children={children}
        setChildren={setChildren}
        infants={infants}
        setInfants={setInfants}
        travelClass={travelClass}
        setTravelClass={setTravelClass}
        onSearch={handleModifySearch}
        showTripTypeSelector={false}
      />

      {/* ── Main Layout ── */}
      <div className="max-w-7xl mx-auto px-4 py-5 relative z-10">
        <div className="flex gap-5">


          {/* ══ LEFT: Filters ══ */}
          <div className="hidden lg:block w-[260px] flex-shrink-0">
            <FilterSidebar
              selectedStops={selectedStops}
              setSelectedStops={setSelectedStops}
              selectedAirlines={selectedAirlines}
              setSelectedAirlines={setSelectedAirlines}
              selectedDepartureTimes={selectedDepartureTimes}
              setSelectedDepartureTimes={setSelectedDepartureTimes}
              selectedArrivalTimes={selectedArrivalTimes}
              setSelectedArrivalTimes={setSelectedArrivalTimes}
              selectedAirports={selectedAirports}
              setSelectedAirports={setSelectedAirports}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              stopCounts={stopCounts}
              uniqueAirlines={uniqueAirlines}
              uniqueAirports={uniqueAirports}
              airlineLogos={airlineLogos}
            />
          </div>


          {/* ══ RIGHT: Results ══ */}
          <div className="flex-1 min-w-0">
            {/* Date Strip - Disabled (will add when implementing multi-date search) */}
            {/*
            <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden mb-4 shadow-sm">
              Date selector will go here
            </div>
            */}

            {/* Sort Bar + Results Count */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-500">
                  Showing <span className="font-bold text-[#1B375B]">{filteredFlights.length}</span> flights
                </p>
                {cheapestPrice && (
                  <span className="text-xs bg-green-50 text-green-700 font-semibold px-2.5 py-1 rounded-full">
                    From ₹{cheapestPrice.toLocaleString()}
                  </span>
                )}
              </div>
              <SortButtons sortBy={sortBy} setSortBy={setSortBy} />
            </div>

            {/* Flight Cards */}
            <div className="space-y-3">
              {/* Loading State - Desktop (skeleton rows for consistency with mobile & roundtrip) */}
              {loading && (
                <div className="space-y-3" data-testid="desktop-flight-skeleton">
                  {[...Array(6)].map((_, i) => (
                    <FlightSkeletonDesktop key={i} />
                  ))}
                </div>
              )}

              {/* Error State - Desktop */}
              {error && !loading && (
                <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-12">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <p className="text-lg text-red-700 font-bold mb-2">{error}</p>
                    <p className="text-sm text-gray-600 mb-6">Please try searching again or modify your search criteria</p>
                    <button
                      onClick={fetchFlights}
                      className="px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-colors"
                    >
                      Retry Search
                    </button>
                  </div>
                </div>
              )}

              {/* No Flights Found - Desktop */}
              {!loading && !error && filteredFlights.length === 0 && (
                <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-12">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Plane className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-lg text-[#1B375B] font-bold mb-2">No flights found</p>
                    <p className="text-sm text-gray-600 mb-6">Try adjusting your search criteria or dates</p>
                    <button
                      onClick={() => setShowModifySearch(true)}
                      className="px-6 py-3 bg-[#1B375B] text-white rounded-xl font-bold hover:bg-[#659A9B] transition-colors"
                    >
                      Modify Search
                    </button>
                  </div>
                </div>
              )}

              {/* Flight Results - Desktop */}
              {!loading && !error && paginatedFlights.map((flight, index) => (
                <React.Fragment key={flight.id}>
                  {/* Promo Banner — after 3rd card */}
                  {index === 3 && (
                    <div className="bg-gradient-to-r from-[#1B375B] via-[#345266] to-[#335E77] rounded-2xl overflow-hidden shadow-md">
                      <div className="flex items-center px-6 py-4 gap-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-[#659A9B] to-[#335E77] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <div className="text-center">
                            <p className="text-white text-xl font-extrabold leading-none">10%</p>
                            <p className="text-white/80 text-[7px] font-bold uppercase tracking-wider">Off</p>
                          </div>
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-lg font-bold">Get 10% Additional Discount</p>
                          <p className="text-white/60 text-sm">on your first booking with code <span className="bg-white/20 text-[#659A9B] font-bold px-2 py-0.5 rounded-md text-xs ml-1">ACFNEW110</span></p>
                        </div>
                        <button className="px-5 py-2.5 bg-white text-[#1B375B] rounded-xl font-bold text-sm hover:bg-[#E7EEEE] transition-all shadow-md flex-shrink-0">
                          Sign Up & Save
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Flight Card */}
                  <div>
                    <FlightCard
                      flight={flight}
                      selectedFare={selectedFares[flight.id]}
                      index={index}
                      cheapestPrice={cheapestPrice}
                      airlineLogos={airlineLogos}
                      isExpanded={expandedFlight === flight.id}
                      onToggleExpand={(id) => setExpandedFlight(expandedFlight === id ? null : id)}
                      onFareSelect={(fare) => {
                        setSelectedFares(prev => ({
                          ...prev,
                          [flight.id]: fare
                        }));
                        // Refetch fare rules if that tab is currently open
                        if (activeTab[flight.id] === 'fareRules' && fare.priceId) {
                          fetchFareRules(fare.priceId, flight.id);
                        }
                      }}
                      onBook={() => {
                        // Build the localStorage payload via the shared helper.
                        // See utils/fareSelection.js for the full field-override
                        // contract — keeps mobile + desktop Book buttons in
                        // sync so the user's chosen fare card propagates
                        // correctly to BookingPage.
                        const fareToBook = selectedFares[flight.id] || flight;
                        const flightDataToSave = buildSelectedFlightPayload(flight, fareToBook);
                        localStorage.setItem('selectedFlight', JSON.stringify(flightDataToSave));
                        localStorage.setItem('searchTimestamp', Date.now().toString());
                        navigate(`/booking?priceId=${fareToBook.priceId}&type=oneway&adults=${adults}&children=${children}&infants=${infants}`);
                      }}
                    />


                    {/* Expanded Flight Details - COMPACT DESIGN */}
                    {expandedFlight === flight.id && (
                      <div className="border-t border-gray-200 bg-gray-50/50 rounded-b-2xl overflow-hidden">
                        {/* Tabs - Compact */}
                        <div className="flex border-b border-gray-200 bg-white px-4">
                          <button
                            onClick={() => setActiveTab(prev => ({ ...prev, [flight.id]: 'journey' }))}
                            className={`px-4 py-2.5 text-xs font-bold transition-all relative ${
                              (activeTab[flight.id] || 'journey') === 'journey'
                                ? 'text-teal-700'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                              </svg>
                              Journey
                            </div>
                            {(activeTab[flight.id] || 'journey') === 'journey' && (
                              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600" />
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setActiveTab(prev => ({ ...prev, [flight.id]: 'fareRules' }));
                              const activePid = selectedFares[flight.id]?.priceId || flight.priceId;
                              fetchFareRules(activePid, flight.id);
                            }}
                            className={`px-5 py-4 text-sm font-bold transition-all relative ${
                              activeTab[flight.id] === 'fareRules'
                                ? 'text-teal-700'
                                : 'text-gray-500 hover:text-gray-700'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Fare Rules & Baggage
                            </div>
                            {activeTab[flight.id] === 'fareRules' && (
                              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-teal-600 rounded-t-sm" />
                            )}
                          </button>
                        </div>

                        {/* Tab Content - Modern Layout */}
                        <div className="px-6 py-5">
                          {/* Journey Tab */}
                          {(activeTab[flight.id] || 'journey') === 'journey' && (
                            <div>
                              {/* Section Header */}
                              <div className="flex items-center gap-2 mb-4">
                                <div className="h-8 w-1 bg-gradient-to-b from-teal-500 to-teal-600 rounded-full"></div>
                                <h4 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Trip Itinerary</h4>
                              </div>

                              {/* Compact Desktop Flight Details Component */}
                              <DesktopFlightDetails
                                flight={flight}
                                displayFlight={{ ...flight, ...(selectedFares[flight.id] || {}) }}
                                airlineLogos={airlineLogos}
                              />
                            </div>
                          )}

                      {/* Fare Rules Tab */}
                      {activeTab[flight.id] === 'fareRules' && (
                            <div>
                              {loadingFareRules[fareRuleKey(flight.id, selectedFares[flight.id]?.priceId || flight.priceId)] ? (
                                <div className="flex items-center justify-center py-8">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#659A9B]"></div>
                                  <span className="ml-3 text-sm text-gray-500">Loading fare rules...</span>
                                </div>
                              ) : fareRules[fareRuleKey(flight.id, selectedFares[flight.id]?.priceId || flight.priceId)]?.error ? (
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                                  <p className="text-sm text-red-600">{fareRules[fareRuleKey(flight.id, selectedFares[flight.id]?.priceId || flight.priceId)].error}</p>
                                </div>
                              ) : (
                                <div className="space-y-3">
                                  <h4 className="text-xs font-bold text-[#1B375B] uppercase tracking-wider">Fare Rules &amp; Conditions</h4>
                                  <FareRulesCompact
                                    normalized={fareRules[fareRuleKey(flight.id, selectedFares[flight.id]?.priceId || flight.priceId)]?._normalized}
                                    compact="default"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              ))}
              
              {/* Load More Button + Infinite Scroll Sentinel (Desktop) */}
              {!loading && !error && hasMoreFlights && (
                <div className="flex flex-col items-center mt-6 px-3 gap-2">
                  <button
                    onClick={handleLoadMore}
                    className="px-8 py-3 bg-white border-2 border-[#659A9B] text-[#1B375B] rounded-xl font-bold hover:bg-[#659A9B] hover:text-white transition-all shadow-sm flex items-center gap-2"
                    data-testid="load-more-flights-desktop-btn"
                  >
                    <span>Load More Flights</span>
                    <span className="bg-[#659A9B] text-white px-2 py-0.5 rounded-full text-xs">
                      +{filteredFlights.length - displayCount}
                    </span>
                  </button>
                  <div ref={infiniteScrollRefDesktop} className="h-1 w-full" aria-hidden="true" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>{/* End Desktop Version */}
      </PullToRefresh>

      {/* Bottom Filter Menu - OUTSIDE PullToRefresh for TRUE FIXED positioning */}
      <div 
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.1)]"
        style={{ zIndex: 9999, position: 'fixed' }}
      >
        <div className="flex items-center justify-around py-3" style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>
          <button
            onClick={() => setShowFilters(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 active:bg-gray-100 rounded-lg transition-colors"
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <Filter className="w-5 h-5 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">FILTER</span>
          </button>

          <button
            onClick={() => {
              setSelectedStops(prev => 
                prev.includes('nonStop') 
                  ? prev.filter(s => s !== 'nonStop') 
                  : [...prev, 'nonStop']
              );
            }}
            className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
              selectedStops.includes('nonStop') ? 'bg-[#1B375B] text-white' : 'text-gray-600 active:bg-gray-100'
            }`}
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              selectedStops.includes('nonStop') ? 'border-white' : 'border-gray-600'
            }`}>
              <div className={`w-2 h-2 rounded-full ${selectedStops.includes('nonStop') ? 'bg-white' : ''}`} />
            </div>
            <span className="text-xs font-medium">NON-STOP</span>
          </button>

          <button
            onClick={() => setShowTimingSheet(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 active:bg-gray-100 rounded-lg transition-colors"
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <Clock className="w-5 h-5 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">TIME</span>
            {selectedDepartureTimes.length > 0 && (
              <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-teal-500" />
            )}
          </button>

          <button
            onClick={() => setShowFilters(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 active:bg-gray-100 rounded-lg transition-colors"
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <Plane className="w-5 h-5 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">AIRLINE</span>
          </button>

          <button
            onClick={() => setShowSortSheet(true)}
            className="flex flex-col items-center gap-1 px-3 py-2 active:bg-gray-100 rounded-lg transition-colors relative"
            style={{ minWidth: '60px', minHeight: '48px' }}
          >
            <ArrowUpDown className="w-5 h-5 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">SORT</span>
            {sortBy !== 'price' && (
              <div className="absolute top-1 right-2 w-2 h-2 rounded-full bg-red-500" />
            )}
          </button>
        </div>
      </div>

      {/* Timing Filter Bottom Sheet */}
      <BottomSheet isOpen={showTimingSheet} onClose={() => setShowTimingSheet(false)} title="Departure Time">
        <div className="p-4 space-y-3">
          {[
            { id: 'earlyMorning', label: 'Early Morning', time: '12am - 6am', icon: '🌙' },
            { id: 'morning', label: 'Morning', time: '6am - 12pm', icon: '🌅' },
            { id: 'afternoon', label: 'Afternoon', time: '12pm - 6pm', icon: '☀️' },
            { id: 'evening', label: 'Evening', time: '6pm - 12am', icon: '🌆' }
          ].map((slot) => (
            <button
              key={slot.id}
              onClick={() => {
                setSelectedDepartureTimes(prev =>
                  prev.includes(slot.id)
                    ? prev.filter(t => t !== slot.id)
                    : [...prev, slot.id]
                );
              }}
              className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                selectedDepartureTimes.includes(slot.id)
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 bg-white active:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{slot.icon}</span>
                <div className="text-left">
                  <p className="font-bold text-[#1B375B]">{slot.label}</p>
                  <p className="text-xs text-gray-500">{slot.time}</p>
                </div>
              </div>
              {selectedDepartureTimes.includes(slot.id) && (
                <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          ))}
          <button
            onClick={() => setShowTimingSheet(false)}
            className="w-full mt-4 bg-gradient-to-r from-[#659A9B] to-[#335E77] text-white font-bold py-3 rounded-lg"
          >
            Apply
          </button>
        </div>
      </BottomSheet>

      {/* Sort Options Bottom Sheet */}
      <BottomSheet isOpen={showSortSheet} onClose={() => setShowSortSheet(false)} title="Sort By">
        <div className="p-4 space-y-3">
          {[
            { id: 'price', label: 'Price', desc: 'Lowest to Highest', icon: '💰' },
            { id: 'duration', label: 'Duration', desc: 'Shortest First', icon: '⏱️' },
            { id: 'departure', label: 'Departure Time', desc: 'Earliest First', icon: '🛫' }
          ].map((option) => (
            <button
              key={option.id}
              onClick={() => {
                setSortBy(option.id);
                setShowSortSheet(false);
              }}
              className={`w-full flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                sortBy === option.id
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 bg-white active:bg-gray-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{option.icon}</span>
                <div className="text-left">
                  <p className="font-bold text-[#1B375B]">{option.label}</p>
                  <p className="text-xs text-gray-500">{option.desc}</p>
                </div>
              </div>
              {sortBy === option.id && (
                <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      </BottomSheet>

      {/* Footer - Hidden on mobile to prevent overlap with bottom filter bar */}
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
};

export default SearchResults;
